import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { solicitudesAPI } from '../../lib/api';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
    ClipboardCheck, Clock, CheckCircle2, XCircle, FileSearch,
    List, ExternalLink, Eye, Copy, Plane, Settings
} from 'lucide-react';
import { toast } from 'sonner';

const estadoColor = {
    creada: 'bg-blue-100 text-blue-800 border-blue-200',
    en_verificacion: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    aprobada: 'bg-green-100 text-green-800 border-green-200',
    rechazada: 'bg-red-100 text-red-800 border-red-200',
};

const estadoLabel = {
    creada: 'Creada',
    en_verificacion: 'En Verificacion',
    aprobada: 'Aprobada',
    rechazada: 'Rechazada',
};

export { estadoColor, estadoLabel };

export default function SolicitudesDashboard() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [recientes, setRecientes] = useState([]);
    const [tipos, setTipos] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            solicitudesAPI.stats().then(r => r.data),
            solicitudesAPI.listar({ estado: '' }).then(r => r.data).catch(() => []),
            solicitudesAPI.getTipos().then(r => r.data).catch(() => []),
        ]).then(([s, sol, t]) => {
            setStats(s);
            setRecientes(sol.slice(0, 5));
            setTipos(t);
        }).catch(() => {}).finally(() => setLoading(false));
    }, []);

    if (loading) return <Layout><div className="flex items-center justify-center h-64"><Clock className="h-6 w-6 animate-spin text-primary" /></div></Layout>;

    const statCards = [
        { label: 'Total', value: stats?.total || 0, icon: ClipboardCheck, color: 'text-primary' },
        { label: 'Nuevas', value: stats?.creadas || 0, icon: Clock, color: 'text-blue-500' },
        { label: 'En Revision', value: stats?.en_verificacion || 0, icon: FileSearch, color: 'text-yellow-500' },
        { label: 'Aprobadas', value: stats?.aprobadas || 0, icon: CheckCircle2, color: 'text-green-500' },
        { label: 'Rechazadas', value: stats?.rechazadas || 0, icon: XCircle, color: 'text-red-500' },
    ];

    const publicFormBase = `${window.location.origin}/solicitudes/publico`;

    const copyLink = (slug) => {
        navigator.clipboard.writeText(`${publicFormBase}/${slug}`);
        toast.success('Link copiado al portapapeles');
    };

    return (
        <Layout>
        <div className="space-y-6" data-testid="solicitudes-dashboard">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold">Solicitudes de Permisos</h1>
                <div className="flex gap-2">
                    {['admin', 'superadmin'].includes(user?.rol) && (
                        <Button variant="outline" onClick={() => navigate('/solicitudes/admin/tipos')} data-testid="btn-config-tipos">
                            <Settings className="h-4 w-4 mr-2" /> Tipos
                        </Button>
                    )}
                    <Button onClick={() => navigate('/solicitudes/lista')} data-testid="btn-ver-todas">
                        <List className="h-4 w-4 mr-2" /> Ver Todas
                    </Button>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {statCards.map(c => (
                    <Card key={c.label} className="cursor-pointer hover:border-primary/40 transition-colors"
                        onClick={() => {
                            if (c.label === 'Total') navigate('/solicitudes/lista');
                            else if (c.label === 'Nuevas') navigate('/solicitudes/lista?estado=creada');
                            else if (c.label === 'En Revision') navigate('/solicitudes/lista?estado=en_verificacion');
                            else if (c.label === 'Aprobadas') navigate('/solicitudes/lista?estado=aprobada');
                            else if (c.label === 'Rechazadas') navigate('/solicitudes/lista?estado=rechazada');
                        }}>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium text-muted-foreground">{c.label}</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex items-center gap-2">
                                <c.icon className={`h-5 w-5 ${c.color}`} />
                                <span className="text-2xl font-bold">{c.value}</span>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Formularios Publicos (links para compartir) */}
            {tipos.length > 0 && (
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                            <Plane className="h-4 w-4" /> Formularios Disponibles
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                        {tipos.map(t => (
                            <div key={t.id} className="flex items-center justify-between border rounded-lg p-3">
                                <div>
                                    <p className="font-medium text-sm">{t.nombre}</p>
                                    <p className="text-xs text-muted-foreground">{t.es_publico ? 'Publico' : 'Solo usuarios'}</p>
                                </div>
                                <div className="flex gap-2">
                                    <Button size="sm" variant="outline" onClick={() => copyLink(t.slug)} data-testid={`copy-link-${t.slug}`}>
                                        <Copy className="h-3 w-3 mr-1" /> Copiar Link
                                    </Button>
                                    <a href={`${publicFormBase}/${t.slug}`} target="_blank" rel="noreferrer">
                                        <Button size="sm" variant="outline" data-testid={`open-form-${t.slug}`}>
                                            <ExternalLink className="h-3 w-3 mr-1" /> Abrir
                                        </Button>
                                    </a>
                                </div>
                            </div>
                        ))}
                    </CardContent>
                </Card>
            )}

            {/* Solicitudes Recientes */}
            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                        <Clock className="h-4 w-4" /> Solicitudes Recientes
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {recientes.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-4">No hay solicitudes aun</p>
                    ) : (
                        <div className="space-y-2">
                            {recientes.map(s => (
                                <div key={s.id}
                                    className="flex items-center justify-between border rounded-lg p-3 cursor-pointer hover:border-primary/40 transition-colors"
                                    onClick={() => navigate(`/solicitudes/detalle/${s.id}`)}
                                    data-testid={`recent-${s.id}`}>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="font-medium text-sm truncate">{s.solicitante?.nombre || 'Sin nombre'}</span>
                                            <Badge className={`text-xs ${estadoColor[s.estado]}`}>{estadoLabel[s.estado]}</Badge>
                                        </div>
                                        <div className="text-xs text-muted-foreground flex gap-3 flex-wrap">
                                            <span>{s.tipo_nombre}</span>
                                            <span>{s.solicitante?.empresa}</span>
                                            <span>{new Date(s.created_at).toLocaleDateString('es-CL')}</span>
                                        </div>
                                    </div>
                                    <Eye className="h-4 w-4 text-muted-foreground" />
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
        </Layout>
    );
}
